import type { CSSProperties } from "react";

/**
 * The desktop's timelines. Each monitor plays for a fixed stretch from the
 * moment it starts, and every moment in that stretch is computed here, at
 * render, as a delay from the start; CSS plays them. Nothing re-renders per
 * tick, and a fixed seed for every random cadence means each render plays the
 * same moments.
 */

/** How long a monitor plays once it starts. Every timeline ends by then. */
export const MONITOR_LIVE_MS = 20_000;

/**
 * A small seeded generator (mulberry32), returning numbers in [0, 1). Seeded,
 * so the server's render and any client that computes the same timeline agree.
 */
export function createRandom(seed: number): () => number {
  let state = seed >>> 0;

  return () => {
    state = (state + 0x6d2b79f5) >>> 0;

    let value = state;
    value = Math.imul(value ^ (value >>> 15), value | 1);
    value ^= value + Math.imul(value ^ (value >>> 7), value | 61);

    return ((value ^ (value >>> 14)) >>> 0) / 4294967296;
  };
}

/** A time of day or a running clock, as hh:mm:ss. */
export function formatClock(totalSeconds: number): string {
  const seconds = totalSeconds % 86_400;
  const parts = [Math.floor(seconds / 3600), Math.floor((seconds % 3600) / 60), seconds % 60];

  return parts.map((part) => String(part).padStart(2, "0")).join(":");
}

/** A CSS delay, for an element whose animation plays at a moment of the timeline. */
export function playsAt(ms: number): { animationDelay: string } {
  return { animationDelay: `${ms}ms` };
}

/* People speak a word at a time, and pause at the end of a clause. */
const WORD_GAP_MS = 300;
const CLAUSE_GAP_MS = 520;

/** When each word of a spoken line is said. */
export function spokenAt(words: readonly string[]): number[] {
  let at = 0;

  return words.map((word) => {
    const saidAt = at;
    at += /[.,]$/.test(word) ? CLAUSE_GAP_MS : WORD_GAP_MS;

    return saidAt;
  });
}

/* A session's answer prints at machine speed: three characters every 20ms, a
   short beat between lines. */
const TYPE_CHARACTERS_PER_TICK = 3;
const TYPE_TICK_MS = 20;
const TYPE_LINE_PAUSE_MS = 320;

export interface TypedChunk {
  text: string;
  at: number;
}

/** Each line split into the chunks it prints in, one line after another. */
export function typedChunks(lines: readonly string[]): TypedChunk[][] {
  let at = 0;

  return lines.map((line) => {
    const chunks: TypedChunk[] = [];

    for (let start = 0; start < line.length; start += TYPE_CHARACTERS_PER_TICK) {
      chunks.push({ text: line.slice(start, start + TYPE_CHARACTERS_PER_TICK), at });
      at += TYPE_TICK_MS;
    }

    // The next line starts a beat after this one's last chunk, not a tick after.
    at += TYPE_LINE_PAUSE_MS - TYPE_TICK_MS;

    return chunks;
  });
}

/* A session working on its own streams a line at a time: it waits, prints the
   line two to five characters per 40ms tick, then waits again. */
const STREAM_TICK_MS = 40;
const STREAM_FIRST_WAIT_MAX_MS = 900;
const STREAM_WAIT_MIN_MS = 150;
const STREAM_WAIT_SPREAD_MS = 1000;
const STREAM_CHARACTERS_MIN = 2;
const STREAM_CHARACTERS_SPREAD = 4;

export interface StreamedLine {
  /** Which script line this is. The stream cycles through the script. */
  scriptIndex: number;
  appearsAt: number;
  /** How many ticks it prints over, the first one as it appears. */
  steps: number;
}

/**
 * The lines a session streams while its monitor plays, starting from
 * `firstScriptIndex`; `script` holds the text each line prints, without its
 * glyph. A line still printing when the monitor settles finishes at that
 * moment, and none starts after it.
 */
export function streamedLines(
  script: readonly string[],
  firstScriptIndex: number,
  seed: number,
): StreamedLine[] {
  const random = createRandom(seed);
  const lines: StreamedLine[] = [];
  let wait = random() * STREAM_FIRST_WAIT_MAX_MS;
  let scriptIndex = firstScriptIndex;
  let printing: (StreamedLine & { printed: number }) | null = null;

  for (let at = STREAM_TICK_MS; at < MONITOR_LIVE_MS; at += STREAM_TICK_MS) {
    if (wait > 0) {
      wait -= STREAM_TICK_MS;
      continue;
    }

    printing ??= { scriptIndex, appearsAt: at, steps: 0, printed: 0 };

    const length = script[scriptIndex % script.length].length;
    printing.printed += STREAM_CHARACTERS_MIN + Math.floor(random() * STREAM_CHARACTERS_SPREAD);
    printing.steps += 1;

    if (printing.printed >= length) {
      lines.push({ scriptIndex, appearsAt: printing.appearsAt, steps: printing.steps });
      scriptIndex += 1;
      printing = null;
      wait = STREAM_WAIT_MIN_MS + random() * STREAM_WAIT_SPREAD_MS;
    }
  }

  // The settle tick prints whatever the line had left.
  if (printing) {
    lines.push({ scriptIndex, appearsAt: printing.appearsAt, steps: printing.steps + 1 });
  }

  return lines;
}

/** The CSS timing that prints a streamed line in its steps, from its first. */
export function printsIn({ appearsAt, steps }: StreamedLine): CSSProperties {
  return {
    animationDelay: `${appearsAt}ms`,
    animationDuration: `${steps * STREAM_TICK_MS}ms`,
    animationTimingFunction: `steps(${steps}, jump-start)`,
  };
}

/* The agents finish a run every second or two. */
const ARRIVAL_WAIT_MIN_MS = 900;
const ARRIVAL_WAIT_SPREAD_MS = 1500;

export interface Arrival {
  at: number;
  /** Seconds on the feed's clock since the monitor started. */
  clockSeconds: number;
}

/** The runs that land in the agents' feed while the machine monitor plays. */
export function agentArrivals(seed: number): Arrival[] {
  const random = createRandom(seed);
  const arrivals: Arrival[] = [];
  let at = 0;
  let clockSeconds = 0;

  for (;;) {
    const wait = ARRIVAL_WAIT_MIN_MS + random() * ARRIVAL_WAIT_SPREAD_MS;
    at += wait;

    if (at >= MONITOR_LIVE_MS) {
      return arrivals;
    }

    clockSeconds += Math.round(wait / 1000);
    arrivals.push({ at: Math.round(at), clockSeconds });
  }
}
